import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  Calendar,
  Clock,
  ExternalLink,
  CheckCircle,
  RefreshCw,
  Building2,
  User,
  Link,
  ChevronRight,
  AlertTriangle,
  Eye,
  Award,
  X
} from 'lucide-react';
import { useCachedData } from '../hooks/useCachedData';
import { useStickyFilter } from '../hooks/useStickyFilter';

// Acknowledged slips are moved out of MasterHS into the "HelpSlip v2 -
// FMS(Backup)" archive (~24k slips since 2024), leaving MasterHS with almost
// no completed rows. The archive is only read when Completed / Closed is
// opened: a raiser gets all of their own slips; the director's all-slips view
// is paged, since everything at once would exceed the API's response cap.
const ALL_SLIPS_PAGE_SIZE = 500;
const ARCHIVE_CACHE_TTL_MS = 5 * 60 * 1000;

const normalizeHeader = (header) => String(header || '').toLowerCase().replace(/\s+/g, ' ').trim();

// Map archive rows by header rather than reusing the master's hard-coded
// indices, so the two sheets' layouts can drift independently. Headers repeat
// ("Planned"/"Actual"), so the acknowledgement pair is taken relative to the
// unique "TimeStamp(Replied)" column.
const archiveRowsToTasks = (rows, { scope, offset }) => {
  const headers = rows[0] || [];
  const find = (match) => headers.findIndex(h => match(normalizeHeader(h)));
  const at = (row, index) => (index >= 0 ? row[index] || '' : '');

  const col = {
    timestamp: find(h => h === 'timestamp'),
    name: find(h => h === 'name'),
    emailId: find(h => h === 'email id'),
    department: find(h => h === 'department'),
    challengeIssue: find(h => h === 'challenge/issue'),
    challengeLevel: find(h => h === 'challenge/issue level'),
    solution1: find(h => h.startsWith('solution 1')),
    solution2: find(h => h === 'solution 2'),
    solution3: find(h => h === 'solution 3'),
    attachment: find(h => h.startsWith('attachment')),
    replyPlanned: find(h => h === 'planned'),
    replyActual: find(h => h === 'actual'),
    raisedBy: find(h => h === 'raised by'),
    assignedTo: find(h => h.startsWith('assigned to')),
    recommendation: find(h => h.startsWith('recommendation')),
    directorTimestamp: find(h => h.startsWith('timestamp(replied')),
    resolveLink: find(h => h.startsWith('resolve')),
    problemResolved: find(h => h === 'problem resolved'),
    remarks: find(h => h.startsWith('remarks')),
    finalTimestamp: find(h => h.startsWith('timestamp(pro')),
  };
  const ackPlanned = col.directorTimestamp >= 0 ? col.directorTimestamp + 1 : -1;
  const ackActual = col.directorTimestamp >= 0 ? col.directorTimestamp + 2 : -1;

  return rows.slice(1)
    .filter(row => row && String(row[0] || '').trim() !== '')
    .map((row, index) => ({
      helpSlipId: row[0] || '',
      timestamp: at(row, col.timestamp),
      name: at(row, col.name),
      emailId: at(row, col.emailId),
      department: at(row, col.department),
      challengeIssue: at(row, col.challengeIssue),
      challengeLevel: at(row, col.challengeLevel),
      solution1: at(row, col.solution1),
      solution2: at(row, col.solution2),
      solution3: at(row, col.solution3),
      attachment: at(row, col.attachment),
      replyPlanned: at(row, col.replyPlanned),
      replyActual: at(row, col.replyActual),
      raisedBy: at(row, col.raisedBy),
      assignedTo: at(row, col.assignedTo),
      repliedBy: at(row, col.assignedTo),
      recommendation: at(row, col.recommendation),
      directorTimestamp: at(row, col.directorTimestamp),
      acknowledgePlanned: at(row, ackPlanned),
      acknowledgeActual: at(row, ackActual),
      resolveLink: at(row, col.resolveLink),
      problemResolved: at(row, col.problemResolved),
      remarks: at(row, col.remarks),
      finalTimestamp: at(row, col.finalTimestamp),
      id: `archive-${scope}-${offset + index}`,
      // Which archive view loaded it: 'mine' (Completed) or 'all' (Closed).
      _scope: scope,
      // No rowNumber: archive rows are history, not new work to notify about.
      _source: 'archive',
    }));
};

// scope 'mine' → every slip this person raised; 'all' → one page of every slip.
const fetchCompletedHelpSlips = async ({ scope, name, offset = 0 }) => {
  const params = new URLSearchParams();
  if (scope === 'all') {
    params.set('all', '1');
    params.set('offset', String(offset));
    params.set('limit', String(ALL_SLIPS_PAGE_SIZE));
  } else {
    params.set('name', name);
  }

  const response = await fetch(`/api/hs-completed?${params}`);
  if (!response.ok) {
    let detail = `HTTP error! status: ${response.status}`;
    try {
      const body = await response.json();
      if (body.error) {
        detail = body.details ? `${body.error} — ${body.details}` : body.error;
      }
    } catch (parseError) {
      // Response wasn't JSON; the status line above is the best detail we have.
      console.warn('Completed help slip error response was not JSON:', parseError);
    }
    throw new Error(detail);
  }

  const data = await response.json();
  const rows = data.values || [];
  const tasks = rows.length > 1 ? archiveRowsToTasks(rows, { scope, offset }) : [];
  return { tasks, total: data.total ?? tasks.length, hasMore: Boolean(data.hasMore) };
};

const isArchived = (task) => task._source === 'archive';

const EMPTY_ARCHIVE = { status: 'idle', tasks: [], total: 0, hasMore: false, error: null };

// Survives unmounts, so leaving and reopening the tab within five minutes
// doesn't re-read the archive. Keyed by scope and user.
const archiveCache = new Map();
const archiveCacheKey = (scope, name) => `${scope}|${name}`;
const readArchiveCache = (scope, name) => {
  const hit = archiveCache.get(archiveCacheKey(scope, name));
  return hit && Date.now() - hit.fetchedAt <= ARCHIVE_CACHE_TTL_MS ? hit.state : null;
};

// Portal Modal Component - Renders outside of dashboard container
const TaskModal = ({ task, onClose, selectedMainTab, selectedSubTab, getTaskLink, getPriorityBadge }) => {
  // Handle escape key and body scroll
  useEffect(() => {
    // Prevent body scroll
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px'; // Prevent layout shift
    
    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    // Cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const getPriorityClass = (level) => {
    const l = level?.toUpperCase();
    if (l === 'HIGH') return 'k-badge k-badge-urgent';
    if (l === 'MEDIUM') return 'k-badge k-badge-medium';
    if (l === 'LOW') return 'k-badge k-badge-low';
    return 'k-badge';
  };

  // Create modal content
  const modalContent = (
    <div className="k-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="k-modal" style={{ maxWidth: '60rem' }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="k-modal-header">
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--gold-light)', marginBottom: '10px' }}>
              HS Details — {task.helpSlipId}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <span className={getPriorityClass(task.challengeLevel)}>{task.challengeLevel || 'Unknown'} Priority</span>
              <span className="k-badge k-badge-role">{selectedMainTab === 'raisedByYou' ? 'Your HS' : 'Director Task'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(() => {
              const taskLink = getTaskLink(task);
              if (taskLink && taskLink.trim() !== '') {
                return (
                  <a href={taskLink} target="_blank" rel="noopener noreferrer" className="k-btn k-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Link size={13} />
                    {selectedMainTab === 'raisedByYou' && selectedSubTab === 'acknowledgePending' ? 'Acknowledge' : 'Reply'}
                  </a>
                );
              }
              return null;
            })()}
            <button className="k-modal-close" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="k-modal-body">
          {/* Challenge/Issue */}
          <div style={{ marginBottom: '20px' }}>
            <p className="k-section-title">Challenge / Issue</p>
            <div className="k-remarks">{task.challengeIssue || 'No challenge/issue description provided'}</div>
          </div>

          {/* Solutions */}
          {(task.solution1 || task.solution2 || task.solution3) && (
            <div style={{ marginBottom: '20px' }}>
              <p className="k-section-title">Solutions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {task.solution1 && (
                  <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--forest-500)' }}>
                    <p className="k-field-label" style={{ marginBottom: '4px' }}>Solution 1 (Best Choice)</p>
                    <p className="k-field-value" style={{ margin: 0 }}>{task.solution1}</p>
                  </div>
                )}
                {task.solution2 && (
                  <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--teal-500)' }}>
                    <p className="k-field-label" style={{ marginBottom: '4px' }}>Solution 2</p>
                    <p className="k-field-value" style={{ margin: 0 }}>{task.solution2}</p>
                  </div>
                )}
                {task.solution3 && (
                  <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold-500)' }}>
                    <p className="k-field-label" style={{ marginBottom: '4px' }}>Solution 3</p>
                    <p className="k-field-value" style={{ margin: 0 }}>{task.solution3}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Director's Recommendation */}
          {(selectedSubTab === 'acknowledgePending' || selectedSubTab === 'completed' || isArchived(task)) && task.recommendation && (
            <div style={{ marginBottom: '20px' }}>
              <p className="k-section-title">Director's Recommendation</p>
              <div style={{ padding: '14px 16px', borderRadius: '10px', background: 'var(--c-success-bg)', border: '1px solid var(--c-success-bg)', borderLeft: '3px solid var(--c-success)' }}>
                {(task.replyActual || task.directorTimestamp) && (
                  <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, fontWeight: 600, color: 'var(--c-success)' }}>
                    <CheckCircle size={13} />
                    Replied {task.replyActual || task.directorTimestamp}
                    {task.repliedBy && <span style={{ fontWeight: 500, opacity: 0.85 }}>· by {task.repliedBy}</span>}
                  </p>
                )}
                <p className="k-field-value" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{task.recommendation}</p>
                {/^https?:\/\//i.test(task.directorAttachment || '') && (
                  <a href={task.directorAttachment} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: 12, fontWeight: 600, color: 'var(--c-success)' }}>
                    <ExternalLink size={12} /> Director's attachment
                  </a>
                )}
                {(task.remarks || task.problemResolved) && (
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--c-success-bg)' }}>
                    <p className="k-field-label" style={{ marginBottom: 4 }}>
                      Your remarks
                      {task.problemResolved && ` · Problem resolved: ${task.problemResolved}`}
                      {task.finalTimestamp && ` · ${task.finalTimestamp}`}
                    </p>
                    <p className="k-field-value" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{task.remarks || '—'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info Grid */}
          <div className="k-info-grid">
            {/* Help Slip Info */}
            <div>
              <p className="k-section-title">Help Slip Information</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Help Slip ID', value: task.helpSlipId },
                  { label: 'Timestamp', value: task.timestamp },
                  { label: 'Name', value: task.name },
                  { label: 'Email ID', value: task.emailId },
                  { label: 'Department', value: task.department },
                ].map((f, i) => f.value && (
                  <div key={i} className="k-info-block">
                    <p className="k-field-label">{f.label}</p>
                    <p className="k-field-value">{f.value}</p>
                  </div>
                ))}
                <div className="k-info-block">
                  <p className="k-field-label">Priority Level</p>
                  <span className={getPriorityClass(task.challengeLevel)}>{task.challengeLevel || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Status Info */}
            <div>
              <p className="k-section-title">Status Information</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {selectedSubTab === 'replyPending' && (
                  <>
                    <div className="k-info-block"><p className="k-field-label">Reply Planned</p><p className="k-field-value">{task.replyPlanned || 'Not set'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Status</p><p className="k-field-value" style={{ color: 'var(--status-warning)', fontWeight: 500 }}>Pending Director Reply</p></div>
                  </>
                )}
                {selectedSubTab === 'acknowledgePending' && (
                  <>
                    <div className="k-info-block"><p className="k-field-label">Reply Actual</p><p className="k-field-value">{task.replyActual || 'N/A'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Acknowledge Planned</p><p className="k-field-value">{task.acknowledgePlanned || 'N/A'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Status</p><p className="k-field-value" style={{ color: 'var(--status-warning)', fontWeight: 500 }}>Pending Your Acknowledgment</p></div>
                  </>
                )}
                {(selectedSubTab === 'completed' || isArchived(task)) && selectedMainTab === 'raisedByYou' && (
                  <>
                    <div className="k-info-block"><p className="k-field-label">Reply Actual</p><p className="k-field-value">{task.replyActual || 'N/A'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Acknowledge Actual</p><p className="k-field-value">{task.acknowledgeActual || 'N/A'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Status</p><p className="k-field-value" style={{ color: 'var(--status-success)', fontWeight: 500 }}>Completed</p></div>
                  </>
                )}
                {selectedMainTab === 'directorTasks' && (
                  <>
                    {isArchived(task) && (
                      <div className="k-info-block"><p className="k-field-label">Status</p><p className="k-field-value" style={{ color: 'var(--status-success)', fontWeight: 500 }}>Completed</p></div>
                    )}
                    <div className="k-info-block"><p className="k-field-label">Reply Planned</p><p className="k-field-value">{task.replyPlanned || 'Not set'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Reply Actual</p><p className="k-field-value">{task.replyActual || 'Not replied'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Acknowledge Planned</p><p className="k-field-value">{task.acknowledgePlanned || 'Not set'}</p></div>
                    <div className="k-info-block"><p className="k-field-label">Acknowledge Actual</p><p className="k-field-value">{task.acknowledgeActual || 'Not acknowledged'}</p></div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Attachment */}
          {task.attachment && (
            <div style={{ marginTop: '16px' }}>
              <p className="k-section-title">Attachment</p>
              <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <p className="k-field-value" style={{ margin: 0 }}>{task.attachment}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

const HSHelpSlip = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();
  // ✅ Set default tabs based on user role
  const isDirector_check = 
    (currentUser?.role?.toLowerCase() === 'director') || 
    (currentUser?.department?.toLowerCase() === 'director');
  
  const [selectedMainTab, setSelectedMainTab] = useState(
    isDirector_check ? 'directorTasks' : 'raisedByYou'
  );
  const [selectedSubTab, setSelectedSubTab] = useState('replyPending');

  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch function for HS data
  const fetchHSData = useCallback(async () => {
  try {
    const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_HS;
    const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_HS;
    
    const apiUrl = '/api/sheets';
    const params = new URLSearchParams({
      sheetId: spreadsheetId,
      sheetName: sheetName,
      range: 'A10:AZ'
    });
    
    const response = await fetch(`${apiUrl}?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const rows = data.values || [];

    if (rows.length === 0) {
      return [];
    }

    const headers = rows[0];
    
    const hsTasks = rows.slice(1)
      .filter(row => row && row.length > 0)
      .map((row, index) => {
        const task = {};
        
        headers.forEach((header, colIndex) => {
          if (header && row[colIndex] !== undefined) {
            const key = cleanHeaderName(header);
            task[key] = row[colIndex];
          }
        });

        // Direct column mapping (keep exactly as existing)
        task.helpSlipId = row[0] || '';           // Column 1
        task.timestamp = row[1] || '';            // Column 2  
        task.name = row[2] || '';                 // Column 3
        task.emailId = row[3] || '';              // Column 4
        task.department = row[4] || '';           // Column 5
        task.challengeIssue = row[5] || '';       // Column 6
        task.challengeLevel = row[6] || '';       // Column 7
        task.solution1 = row[7] || '';            // Column 8
        task.solution2 = row[8] || '';            // Column 9
        task.solution3 = row[9] || '';            // Column 10
        task.attachment = row[10] || '';          // Column 11
        task.problemSolvingLink1 = row[13] || ''; // Column 12
        task.problemSolvingLink = row[13] || '';  // Column 13
        task.replyLink = row[13] || row[14] || '';           // Column 14
        task.newLink = row[14] || '';             // Column 15
        task.replyPlanned = row[18] || '';        // Column 19
        task.replyActual = row[19] || '';         // Column 20
        task.replyTimeDelay = row[20] || '';      // Column 21
        
        // Director Reply Section (keep exactly as existing)
        task.helpSlipId2 = row[18] || '';         // Column 19
        task.raisedBy = row[19] || '';            // Column 20
        task.assignedTo = row[20] || '';          // Column 21
        // assignedTo above actually reads "Time Delay"; left as is because the
        // sidebar/Overview counts read it. The replier is "Assigned To (Doer)".
        task.repliedBy = row[23] || '';           // Column 24
        task.recommendation = row[24] || '';      // Column 25
        task.directorAttachment = row[25] || '';  // Column 26
        task.directorTimestamp = row[26] || '';   // Column 27
        task.acknowledgePlanned = row[27] || '';  // Column 28
        task.acknowledgeActual = row[28] || '';   // Column 29
        task.acknowledgeTimeDelay = row[29] || '';// Column 30
        task.resolveLink = row[30] || '';         // Column 31
        
        // Final Section (keep exactly as existing)
        task.helpSlipId3 = row[28] || '';         // Column 29
        task.doerRaisedBy = row[29] || '';        // Column 30
        task.problemResolved = row[33] || '';     // Column 34
        task.remarks = row[34] || '';             // Column 35
        task.finalTimestamp = row[35] || '';      // Column 36

        task.id = index + 1;
        task.rowNumber = index + 11;
        
        return task;
      })
      // ✅ ONLY NEW ADDITION: Filter by user at fetch level (before caching)
      .filter(task => {
        // Check if user is director (same logic as existing component)
        const isDirector = 
          (currentUser?.role?.toLowerCase() === 'director') || 
          (currentUser?.department?.toLowerCase() === 'director');
        
        if (isDirector) {
          // For directors: NO FILTERING - load all data
          return true;
        } else {
          // For non-directors: load only tasks raised by the user
          return task.name === currentUser.name;
        }
      })
      // ✅ Additional filter: Only include tasks with valid helpSlipId
      .filter(task => {
        return task.helpSlipId && task.helpSlipId.trim() !== '';
      });

    // ✅ Add logging to track filtering effectiveness
    console.log(`✅ HS Data filtered for ${currentUser.name}: ${hsTasks.length} relevant tasks out of ${rows.length - 1} total rows`);

    return hsTasks;
  } catch (error) {
    console.error('Error fetching HS data:', error);
    throw error;
  }
}, [currentUser.name, currentUser.role, currentUser.department]);

  const { data: activeTasks, loading, error, refresh: reload } = useCachedData(
    'hs',
    currentUser,
    fetchHSData
  );

  // Completed slips from the archive, loaded on demand per scope.
  const [archives, setArchives] = useState(() => ({
    mine: readArchiveCache('mine', currentUser.name) || EMPTY_ARCHIVE,
    all: readArchiveCache('all', currentUser.name) || EMPTY_ARCHIVE,
  }));
  // Re-entry guard: the status in state lags a render behind a fast double click.
  const archiveInFlight = useRef({ mine: false, all: false });

  const loadArchive = useCallback(async (scope, { more = false } = {}) => {
    if (archiveInFlight.current[scope]) return;
    archiveInFlight.current[scope] = true;
    // Paint the placeholder before the request goes out.
    setArchives(prev => ({
      ...prev,
      [scope]: more
        ? { ...prev[scope], status: 'loadingMore', error: null }
        : { ...EMPTY_ARCHIVE, status: 'loading' },
    }));
    try {
      const offset = more ? archives[scope].tasks.length : 0;
      const page = await fetchCompletedHelpSlips({ scope, name: currentUser.name, offset });
      setArchives(prev => {
        const tasks = more ? prev[scope].tasks.concat(page.tasks) : page.tasks;
        const next = { status: 'ready', tasks, total: page.total, hasMore: page.hasMore, error: null };
        archiveCache.set(archiveCacheKey(scope, currentUser.name), { state: next, fetchedAt: Date.now() });
        return { ...prev, [scope]: next };
      });
    } catch (err) {
      console.error(`Error loading completed help slips (${scope}):`, err);
      setArchives(prev => ({
        ...prev,
        // Keep what was already loaded on a failed "load more".
        [scope]: { ...prev[scope], status: 'error', error: err.message || 'Could not reach the Help Slip archive' },
      }));
    } finally {
      archiveInFlight.current[scope] = false;
    }
  }, [archives, currentUser.name]);

  // Lazy: the archive is read the first time Completed / Closed is opened.
  const archiveScope = selectedSubTab === 'completed' ? 'mine' : selectedSubTab === 'closed' ? 'all' : null;
  useEffect(() => {
    if (archiveScope && archives[archiveScope].status === 'idle') loadArchive(archiveScope);
  }, [archiveScope, archives, loadArchive]);
  const archive = archiveScope ? archives[archiveScope] : null;

  // A slip lives in one sheet or the other; prefer the master row if a move
  // happens to be mid-flight.
  const allTasks = useMemo(() => {
    const activeIds = new Set(activeTasks.map(task => task.helpSlipId));
    return activeTasks.concat(
      archives.mine.tasks.filter(task => !activeIds.has(task.helpSlipId)),
      archives.all.tasks.filter(task => !activeIds.has(task.helpSlipId))
    );
  }, [activeTasks, archives]);

  // Add local loading state for manual refresh
const [isManualRefreshing, setIsManualRefreshing] = useState(false);
const refreshInFlight = useRef(false);

// Force a fresh fetch from the server. Goes through the hook so the list on
// screen updates, not just the cache.
const refresh = useCallback(async () => {
  if (refreshInFlight.current) return;
  refreshInFlight.current = true;
  setIsManualRefreshing(true);
  try {
    await reload(true);
    // Re-read the archive view on screen too; other scopes reload on next open.
    if (archiveScope) {
      archiveCache.delete(archiveCacheKey(archiveScope, currentUser.name));
      await loadArchive(archiveScope);
    }
  } catch (error) {
    // The hook already surfaces fetch errors in its error state.
    console.error('❌ Error during Help Slip refresh:', error);
  } finally {
    refreshInFlight.current = false;
    setIsManualRefreshing(false);
  }
}, [reload, archiveScope, loadArchive, currentUser.name]);

// Combined loading state
const isRefreshing = loading || isManualRefreshing;

  const cleanHeaderName = (header) => {
    return header
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const isDirector = 
  (currentUser?.role?.toLowerCase() === 'director') || 
  (currentUser?.department?.toLowerCase() === 'director');

  const getTasksForMainTab = () => {  
  let filteredTasks;
  
  if (selectedMainTab === 'raisedByYou') {
    filteredTasks = allTasks.filter(task => task.name === currentUser.name);
  } else if (selectedMainTab === 'directorTasks') {
    // ✅ NEW: Handle director tasks specifically
    filteredTasks = allTasks.filter(task => task.helpSlipId && task.helpSlipId.trim() !== '');
  } else {
    filteredTasks = allTasks.filter(task => task.helpSlipId && task.helpSlipId.trim() !== '');
  }

  // Sort by timestamp in descending order (newest first)
  return filteredTasks.sort((a, b) => {
    // Handle cases where timestamp might be missing or invalid
    const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
    const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
    return dateB - dateA; // Descending order
  });
};

  const getSubTabs = () => {
    if (selectedMainTab === 'raisedByYou') {
      return [
        { key: 'replyPending', label: 'Reply Pending' },
        { key: 'acknowledgePending', label: 'Acknowledge Pending' },
        { key: 'completed', label: 'Completed' }
      ];
    } else if (isDirector) {
      return [
        { key: 'replyPending', label: 'Reply Pending' },
        { key: 'replied', label: 'Replied' },
        { key: 'closed', label: 'Closed' }
      ];
    }
    return [];
  };

  const getFilteredTasks = () => {
    let filteredTasks = getTasksForMainTab();

    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        task.helpSlipId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.challengeIssue.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.department.toLowerCase().includes(searchTerm.toLowerCase())
);
    }

    // Archived slips are closed: they belong only in Completed / Closed.
    // Archived slips are closed: 'mine' rows belong only in Completed, the
    // all-slips page only in Closed (kept in the server's newest-first order).
    if (selectedSubTab === 'closed') {
      const closedIds = new Set(filteredTasks.filter(t => t._scope === 'all').map(t => t.id));
      return archives.all.tasks.filter(t => closedIds.has(t.id));
    }
    filteredTasks = filteredTasks.filter(task =>
      !isArchived(task) || (selectedSubTab === 'completed' && task._scope === 'mine'));

    if (selectedMainTab === 'raisedByYou') {
      if (selectedSubTab === 'replyPending') {
        // Column 16 (Planned) is not null AND Column 17 (Actual) is null
        filteredTasks = filteredTasks.filter(task =>
          task.replyPlanned && task.replyPlanned.trim() !== '' &&
          (!task.replyActual || task.replyActual.trim() === '')
        );
      } else if (selectedSubTab === 'acknowledgePending') {
        // Column 17 (Actual) is not null AND Column 25 (Planned) is not null AND Column 26 (Actual) is null
        filteredTasks = filteredTasks.filter(task => 
          task.replyActual && task.replyActual.trim() !== '' &&
          task.acknowledgePlanned && task.acknowledgePlanned.trim() !== '' &&
          (!task.acknowledgeActual || task.acknowledgeActual.trim() === '')
        );
      } else if (selectedSubTab === 'completed') {
        // Column 17 (Actual) is not null AND Column 25 (Planned) is not null AND Column 26 (Actual) is not null
        filteredTasks = filteredTasks.filter(task => isArchived(task) || (
          task.replyActual && task.replyActual.trim() !== '' &&
          task.acknowledgePlanned && task.acknowledgePlanned.trim() !== '' &&
          task.acknowledgeActual && task.acknowledgeActual.trim() !== ''
        ));
      }
    } else if (isDirector) {
      if (selectedSubTab === 'replyPending') {
        filteredTasks = filteredTasks.filter(task => 
          (!task.replyActual || task.replyActual.trim() === '')
        );
      } else if (selectedSubTab === 'replied') {
        filteredTasks = filteredTasks.filter(task => 
          task.replyActual && task.replyActual.trim() !== ''
        );
      }
    }

    return filteredTasks;
  };

  const getPriorityBadge = (level) => {
    switch (level?.toUpperCase()) {
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskLink = (task) => {
  if (selectedMainTab === 'raisedByYou') {
    if (selectedSubTab === 'acknowledgePending') {
      return task.resolveLink;
    }
    return null;
  } else if (selectedMainTab === 'directorTasks') {
    // ✅ NEW: Handle director tasks
    if (selectedSubTab === 'replyPending') {
      return task.replyLink; // Show reply link for directors
    }
    return null;
  } else if (isDirector && selectedSubTab === 'replyPending') {
    // Keep existing logic for backward compatibility
    return task.replyLink;
  }
  return null;
};

  const getStats = () => {
    const raisedByYouTasks = allTasks.filter(task => task.name === currentUser.name && task._scope !== 'all');
    const activeRaisedByYou = raisedByYouTasks.filter(task => !isArchived(task));
    // Director cards describe live MasterHS work only; archived slips would
    // otherwise inflate the total and the reply rate's denominator.
    const directorTasks = allTasks.filter(task => !isArchived(task) && task.helpSlipId && task.helpSlipId.trim() !== '');

    const replyPending = activeRaisedByYou.filter(task => 
      task.replyPlanned && task.replyPlanned.trim() !== '' &&
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;

    const acknowledgePending = activeRaisedByYou.filter(task => 
      task.replyActual && task.replyActual.trim() !== '' &&
      task.acknowledgePlanned && task.acknowledgePlanned.trim() !== '' &&
      (!task.acknowledgeActual || task.acknowledgeActual.trim() === '')
    ).length;

    const completed = raisedByYouTasks.filter(task => isArchived(task) || (
      task.replyActual && task.replyActual.trim() !== '' &&
      task.acknowledgePlanned && task.acknowledgePlanned.trim() !== '' &&
      task.acknowledgeActual && task.acknowledgeActual.trim() !== ''
    )).length;

    const directorReplyPending = directorTasks.filter(task => 
      (!task.replyActual || task.replyActual.trim() === '')
    ).length;

    const directorReplied = directorTasks.filter(task => 
      task.replyActual && task.replyActual.trim() !== ''
    ).length;

    return {
      raisedByYou: raisedByYouTasks.length,
      replyPending,
      acknowledgePending,
      completed: archives.mine.status === 'idle' ? null : completed,
      directorTasks: directorTasks.length,
      directorReplyPending,
      directorReplied,
      directorClosed: archives.all.status === 'idle' ? null : archives.all.total
    };
  };

  const filteredTasks = getFilteredTasks();
  const stats = getStats();
  const subTabs = getSubTabs();

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading">
          <div className="k-loading-spinner" />
          <p>Loading Help Slips…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-page">
        <div className="k-card k-error" style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertTriangle size={36} style={{ margin: '0 auto 12px', color: 'var(--status-error)' }} />
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Help Slips</p>
          <p style={{ marginBottom: 16 }}>{error}</p>
          <button className="k-btn-primary" onClick={refresh}>
            <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Priority border color for task rows
  const getPriorityBorderColor = (level) => {
    const l = level?.toUpperCase();
    if (l === 'HIGH') return 'var(--status-error)';
    if (l === 'MEDIUM') return 'var(--gold-500,#c8a03c)';
    return 'var(--forest-400,#4a8c6e)';
  };

  return (
    <div className="k-page">

      {/* Stats grid — dynamic by main tab */}
      <div className="k-stats-grid">
        {selectedMainTab === 'raisedByYou' ? (<>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
              <FileText size={20} style={{ color: 'var(--forest-600,#2d6a50)' }} />
            </div>
            <div>
              <div className="k-stat-value">{stats.raisedByYou}</div>
              <div className="k-stat-label">Raised By You</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'var(--gold-100,#fef3cd)' }}>
              <Clock size={20} style={{ color: 'var(--gold-600,#a07020)' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: 'var(--gold-600,#a07020)' }}>{stats.replyPending}</div>
              <div className="k-stat-label">Reply Pending</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'rgba(249,115,22,0.12)' }}>
              <Eye size={20} style={{ color: 'var(--status-warning,#f97316)' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: 'var(--status-warning,#f97316)' }}>{stats.acknowledgePending}</div>
              <div className="k-stat-label">Ack Pending</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'rgba(34,197,94,0.12)' }}>
              <CheckCircle size={20} style={{ color: 'var(--status-success,#22c55e)' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: 'var(--status-success,#22c55e)' }}
                title={stats.completed === null ? 'Open the Completed tab to load your completed slips' : undefined}>
                {stats.completed === null ? '—' : stats.completed}
              </div>
              <div className="k-stat-label">Completed</div>
            </div>
          </div>
        </>) : (<>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
              <FileText size={20} style={{ color: 'var(--forest-600,#2d6a50)' }} />
            </div>
            <div>
              <div className="k-stat-value">{stats.directorTasks}</div>
              <div className="k-stat-label">Total Help Slips</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'var(--gold-100,#fef3cd)' }}>
              <Clock size={20} style={{ color: 'var(--gold-600,#a07020)' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: 'var(--gold-600,#a07020)' }}>{stats.directorReplyPending}</div>
              <div className="k-stat-label">Reply Pending</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'rgba(34,197,94,0.12)' }}>
              <CheckCircle size={20} style={{ color: 'var(--status-success,#22c55e)' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: 'var(--status-success,#22c55e)' }}>{stats.directorReplied}</div>
              <div className="k-stat-label">Replied</div>
            </div>
          </div>
          <div className="k-stat-card">
            <div className="k-stat-icon" style={{ background: 'rgba(139,92,246,0.12)' }}>
              <Award size={20} style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <div className="k-stat-value" style={{ color: '#7c3aed' }}>
                {stats.directorTasks > 0 ? Math.round((stats.directorReplied / stats.directorTasks) * 100) : 0}%
              </div>
              <div className="k-stat-label">Completion Rate</div>
            </div>
          </div>
        </>)}
      </div>

      {/* Main tabs */}
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>

        <div className="k-tabs-wrap">
          <div className="k-tabs-main" style={{ padding: '0 20px' }}>
            <button
              className={`k-tab-btn${selectedMainTab === 'raisedByYou' ? ' active' : ''}`}
              onClick={() => { setSelectedMainTab('raisedByYou'); setSelectedSubTab('replyPending'); }}
            >
              HS Raised By You
              <span className="k-tab-count">{stats.raisedByYou}</span>
            </button>
            {isDirector && (
              <button
                className={`k-tab-btn${selectedMainTab === 'directorTasks' ? ' active' : ''}`}
                onClick={() => { setSelectedMainTab('directorTasks'); setSelectedSubTab('replyPending'); }}
              >
                Director Tasks
                <span className="k-tab-count">{stats.directorTasks}</span>
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Sub tabs + search — pinned while the list scrolls */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`} style={{ padding: 0, overflow: 'hidden' }}>
        {/* Sub tabs */}
        {subTabs.length > 0 && (
          <div className="k-tabs-wrap" style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-2)' }}>
            <div className="k-tabs-main" style={{ padding: '0 20px' }}>
              {subTabs.map((tab) => {
                let tabCount = 0;
                if (selectedMainTab === 'raisedByYou') {
                  if (tab.key === 'replyPending') tabCount = stats.replyPending;
                  else if (tab.key === 'acknowledgePending') tabCount = stats.acknowledgePending;
                  else if (tab.key === 'completed') tabCount = stats.completed;
                } else {
                  if (tab.key === 'replyPending') tabCount = stats.directorReplyPending;
                  else if (tab.key === 'replied') tabCount = stats.directorReplied;
                  else if (tab.key === 'closed') tabCount = stats.directorClosed;
                }
                return (
                  <button
                    key={tab.key}
                    className={`k-tab-btn${selectedSubTab === tab.key ? ' active' : ''}`}
                    onClick={() => setSelectedSubTab(tab.key)}
                    style={selectedSubTab === tab.key ? { borderBottomColor: 'var(--gold-500,#c8a03c)', color: 'var(--gold-600,#a07020)' } : {}}
                  >
                    {tab.label}
                    {tabCount !== null && <span className="k-tab-count">{tabCount}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="k-filter-bar" style={{ padding: '12px 16px' }}>
          <input
            type="text"
            className="k-search"
            placeholder="Search help slips…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <button
            onClick={refresh}
            disabled={isRefreshing}
            title="Refresh"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'var(--bg-surface-2)', border: '1px solid var(--border)',
              cursor: isRefreshing ? 'not-allowed' : 'pointer', color: 'var(--text-muted)',
            }}
          >
            <RefreshCw size={13} style={{ transition: 'transform 0.6s', transform: isRefreshing ? 'rotate(360deg)' : 'none' }} />
          </button>
        </div>

      </div>

      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Task list */}
        {archive && archive.status === 'loading' && filteredTasks.length === 0 ? (
          <div className="k-loading" style={{ padding: '3rem' }}>
            <div className="k-loading-spinner" />
            <p>{archiveScope === 'all'
              ? `Loading the latest ${ALL_SLIPS_PAGE_SIZE} completed help slips…`
              : 'Loading all your completed help slips…'}</p>
          </div>
        ) : archive && archive.status === 'error' && filteredTasks.length === 0 ? (
          <div className="k-error" style={{ padding: '2rem', textAlign: 'center' }}>
            <AlertTriangle size={28} style={{ margin: '0 auto 10px', color: 'var(--status-error)' }} />
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Couldn't load completed help slips</p>
            <p style={{ marginBottom: 14 }}>{archive.error}</p>
            <button className="k-btn-primary" onClick={() => loadArchive(archiveScope)}>
              <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
              Retry
            </button>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="k-empty" style={{ padding: '3rem' }}>
            <FileText size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontWeight: 600 }}>No Help Slips Found</p>
            <p>
              {archive && !searchTerm
                ? (archiveScope === 'all' ? 'No completed help slips yet.' : "You don't have any completed help slips yet.")
                : getTasksForMainTab().length === 0
                ? `No slips in "${selectedMainTab === 'raisedByYou' ? 'HS Raised By You' : 'Director Tasks'}"`
                : 'No slips match the current filters'}
            </p>
          </div>
        ) : (
          <div className="k-task-list">
            {filteredTasks.map((task, index) => {
              const taskLink = getTaskLink(task);
              const priorityLevel = task.challengeLevel?.toUpperCase();
              return (
                <div
                  key={index}
                  className="k-task-row"
                  style={{ borderLeft: `3px solid ${getPriorityBorderColor(task.challengeLevel)}` }}
                  onClick={() => { setSelectedTask(task); setShowModal(true); }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="k-task-title">
                      <span className="k-badge k-badge-role">{task.helpSlipId}</span>
                      {priorityLevel === 'HIGH' && <span className="k-badge k-badge-urgent">{task.challengeLevel}</span>}
                      {priorityLevel === 'MEDIUM' && <span className="k-badge k-badge-medium">{task.challengeLevel}</span>}
                      {priorityLevel === 'LOW' && <span className="k-badge k-badge-low">{task.challengeLevel}</span>}
                      {isArchived(task) && <span className="k-badge k-badge-completed">Completed</span>}
                      <span className="k-badge" style={{ background: 'rgba(139,92,246,0.12)', color: '#7c3aed' }}>
                        {selectedMainTab === 'raisedByYou' ? 'Your HS' : 'Director Task'}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0 6px', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {task.challengeIssue}
                    </p>
                    <div className="k-task-meta">
                      <span className="k-task-meta-item">
                        <User size={11} />
                        {selectedMainTab === 'raisedByYou' ? `By: ${task.name}` : `From: ${task.name}`}
                      </span>
                      <span className="k-task-meta-item">
                        <Building2 size={11} />
                        {task.department}
                      </span>
                      <span className="k-task-meta-item">
                        <Calendar size={11} />
                        {task.timestamp}
                      </span>
                      {selectedSubTab === 'replyPending' && task.replyPlanned && (
                        <span className="k-task-meta-item" style={{ color: 'var(--gold-600,#a07020)' }}>
                          <Clock size={11} />
                          Reply Due: {task.replyPlanned}
                        </span>
                      )}
                      {selectedSubTab === 'acknowledgePending' && task.acknowledgePlanned && (
                        <span className="k-task-meta-item" style={{ color: 'var(--status-warning,#f97316)' }}>
                          <Clock size={11} />
                          Ack Due: {task.acknowledgePlanned}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                    {taskLink && taskLink.trim() !== '' && (
                      <a
                        href={taskLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="k-btn-primary"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                        title={selectedMainTab === 'raisedByYou' && selectedSubTab === 'acknowledgePending' ? 'Acknowledge Link' : 'Reply Link'}
                      >
                        <ExternalLink size={11} />
                        {selectedMainTab === 'raisedByYou' && selectedSubTab === 'acknowledgePending' ? 'Acknowledge' : 'Reply'}
                      </a>
                    )}
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Archive footer: in-progress, failed, or more to load — only once rows are on screen */}
        {archive && filteredTasks.length > 0 && (archive.status !== 'ready' || archiveScope === 'all') && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--text-muted)' }}>
            {archive.status === 'loading' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <RefreshCw size={13} className="animate-spin" />
                Loading completed slips from the archive…
              </span>
            ) : archive.status === 'error' ? (
              <>
                <span style={{ color: 'var(--status-error)' }}>Couldn't load more completed slips — {archive.error}</span>
                <button className="k-btn-primary" onClick={() => loadArchive(archiveScope, { more: archive.tasks.length > 0 })}
                  style={{ fontSize: 12, padding: '4px 10px' }}>
                  Retry
                </button>
              </>
            ) : archiveScope === 'all' ? (
              <>
                <span>{`Showing ${archive.tasks.length.toLocaleString()} of ${archive.total.toLocaleString()} completed slips, newest first`}</span>
                {(archive.hasMore || archive.status === 'loadingMore') && (
                  <button
                    className="k-btn-primary"
                    onClick={() => loadArchive('all', { more: true })}
                    disabled={archive.status === 'loadingMore'}
                    title={archive.status === 'loadingMore' ? 'Loading…' : `Load the next ${ALL_SLIPS_PAGE_SIZE}`}
                    style={{ fontSize: 12, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: archive.status === 'loadingMore' ? 'not-allowed' : 'pointer' }}
                  >
                    {archive.status === 'loadingMore' && <RefreshCw size={12} className="animate-spin" />}
                    {archive.status === 'loadingMore' ? `Loading ${ALL_SLIPS_PAGE_SIZE} more…` : `Load ${ALL_SLIPS_PAGE_SIZE} more`}
                  </button>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Portal Modal */}
      {showModal && selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => { setShowModal(false); setSelectedTask(null); }}
          selectedMainTab={selectedMainTab}
          selectedSubTab={selectedSubTab}
          getTaskLink={getTaskLink}
          getPriorityBadge={getPriorityBadge}
        />
      )}
    </div>
  );
};

export default HSHelpSlip;
